import { getUrl } from "src/basepath";
import { capture } from "./analytics.ts";

const SLOW_REQUEST_THRESHOLD_MS = 2000;

export interface NetworkEvent {
  url: string;
  method: string;
  status: number;
  duration: number;
  ok: boolean;
  error?: string;
}

const TRACKING_IGNORE_PATTERNS = [
  "posthog.com",
  "i.posthog.com",
  "/e/", // PostHog's event endpoint
];

// PostHog's same-origin reverse proxy (nginx.prod.conf). Matched as a path
// prefix: "/a/" alone would also match unrelated URLs.
const POSTHOG_PROXY_PATH = new URL(getUrl("/a/")).pathname;

function shouldTrack(url: string): boolean {
  if (new URL(url, window.location.origin).pathname.startsWith(POSTHOG_PROXY_PATH)) {
    return false;
  }
  return !TRACKING_IGNORE_PATTERNS.some((pattern) => url.includes(pattern));
}

let isInitialized = false;

export function initNetworkTracker() {
  if (isInitialized) return;
  isInitialized = true;

  const originalFetch = window.fetch;

  window.fetch = async (input, init) => {
    let url: string;
    if (typeof input === "string") {
      url = input;
    } else if (input instanceof Request) {
      url = input.url;
    } else if (input instanceof URL) {
      url = input.toString();
    } else {
      url = "";
    }

    // Skip tracking for PostHog and other analytics
    if (!shouldTrack(url)) {
      return originalFetch(input, init);
    }

    const startTime = performance.now();
    const method = init?.method || "GET";

    try {
      const response = await originalFetch(input, init);
      const duration = performance.now() - startTime;

      if (!response.ok || duration > SLOW_REQUEST_THRESHOLD_MS) {
        capture("fetch_request", {
          url,
          method,
          status: response.status,
          duration: Math.round(duration),
          ok: response.ok,
        });
      }

      return response;
    } catch (error) {
      const duration = performance.now() - startTime;

      capture("fetch_request", {
        url,
        method,
        status: 0,
        duration: Math.round(duration),
        ok: false,
        error: error instanceof Error ? error.message : "Unknown error",
      });

      throw error;
    }
  };
}
